import React, { useState, useEffect } from 'react'
import { Form, Select, Button, Tag, Input, Modal, Row, Col, Card } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPills, faAppleAlt, faAllergies } from '@fortawesome/free-solid-svg-icons'
import uniqBy from 'lodash'
import '../style/DesktopAllergies.css'
import {
  ALLERGY_CATEGORIES,
  COMMON_ALLERGIES,
  COMMON_FOOD_ALLERGIES,
  COMMON_GENERAL_ALLERGIES
} from '../../utils/constants'
import useAllergies from '../../hooks/useAllergies'
import DesktopMedicalHistoryNav from './Nav'
import DesktopSectionHeader from './DesktopSectionHeader'

const DesktopAllergies = ({ clientForm, loading, setFacet, setSaving }) => {
  const {
    addFoodAllergy,
    addGeneralAllergy,
    additionalFoodAllergies,
    additionalGeneralAllergies,
    additionalMedications,
    debouncedNappiFilter,
    handleFoodOptionClick,
    handleGeneralOptionClick,
    handleOptionClick,
    handleSave,
    isModalVisible,
    nappiData,
    nappiLoading,
    removeFoodAllergy,
    removeGeneralAllergy,
    selectedAllergies,
    selectedCategory,
    selectedFoodAllergies,
    selectedGeneralAllergies,
    setAdditionalMedications,
    setIsModalVisible,
    setNappiData,
    setSelectedCategory
  } = useAllergies({ clientForm, setSaving })

  const [newFoodAllergy, setNewFoodAllergy] = useState('')
  const [newGeneralAllergy, setNewGeneralAllergy] = useState('')

  // Scroll to top when component mounts
  useEffect(() => {
    setTimeout(() => {
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }, 0)
  }, [])

  const addMedication = (name) => {
    if (name && !additionalMedications.includes(name)) {
      setAdditionalMedications([...additionalMedications, name])
    }
  }

  const removeMedication = (name) => {
    setAdditionalMedications(additionalMedications.filter((med) => med !== name))
  }

  const handleAddFoodAllergy = () => {
    if (newFoodAllergy.trim()) {
      addFoodAllergy(newFoodAllergy.trim())
      setNewFoodAllergy('')
      setIsModalVisible(false)
    }
  }

  const handleAddGeneralAllergy = () => {
    if (newGeneralAllergy.trim()) {
      addGeneralAllergy(newGeneralAllergy.trim())
      setNewGeneralAllergy('')
      setIsModalVisible(false)
    }
  }

  return (
    <div className='desktop-allergies-container'>
      {!selectedCategory ? (
        <>
          <DesktopSectionHeader
            icon={faAllergies}
            title='Allergies'
            subtitle='Select a category to manage your allergies'
          />
          <div className='desktop-allergies-content'>
            <Row gutter={[16, 16]}>
              {ALLERGY_CATEGORIES.map((category) => (
                <Col xs={24} sm={12} md={8} key={category.id}>
                  <Card
                    className='desktop-category-card'
                    hoverable
                    onClick={() => setSelectedCategory(category.id)}
                    style={{ '--category-color': category.color }}
                  >
                    <div className='desktop-category-content'>
                      <div className='desktop-category-icon'>
                        <FontAwesomeIcon icon={category.icon} />
                      </div>
                      <h3>{category.label}</h3>
                      <p>{category.description}</p>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </>
      ) : selectedCategory === 'medications' ? (
        <>
          <DesktopSectionHeader
            icon={faPills}
            title='Medications'
            subtitle="Select any medications you're allergic to"
            onBack={() => setSelectedCategory(null)}
          />
          <div className='desktop-allergies-content'>
            <Card className='desktop-allergy-section-card'>
              <Row gutter={[16, 16]}>
                {COMMON_ALLERGIES.map((allergy) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={allergy.id}>
                    <div
                      className={`desktop-allergy-option ${selectedAllergies.includes(allergy.id) ? 'selected' : ''}`}
                      role='checkbox'
                      onClick={() => handleOptionClick(allergy)}
                      aria-checked={selectedAllergies.includes(allergy.id)}
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleOptionClick(allergy)
                        }
                      }}
                    >
                      <div className='desktop-allergy-icon'>
                        <FontAwesomeIcon icon={faPills} />
                      </div>
                      <span className='desktop-allergy-name'>{allergy.label}</span>
                      <Form.Item
                        name={['allergies', 'meds', allergy.id]}
                        valuePropName='checked'
                        style={{ display: 'none' }}
                      >
                        <input type='checkbox' />
                      </Form.Item>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>

            <div className='desktop-additional-medications'>
              <Button type='primary' className='desktop-add-medication-btn' onClick={() => setIsModalVisible(true)}>
                Add Medications
              </Button>

              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                {additionalMedications.map((medication, index) => (
                  <Col key={index}>
                    <Tag closable onClose={() => removeMedication(medication)} className='desktop-allergy-tag'>
                      {medication}
                    </Tag>
                  </Col>
                ))}
              </Row>
            </div>

            <Modal
              title='Search and Add Medications'
              open={isModalVisible}
              onCancel={() => {
                setIsModalVisible(false)
                setNappiData([])
              }}
              footer={null}
              className='desktop-allergies-modal'
            >
              <Select
                showSearch
                placeholder='Search medications...'
                loading={nappiLoading}
                onSearch={debouncedNappiFilter}
                onSelect={(value) => {
                  addMedication(value)
                  setNappiData([])
                  setIsModalVisible(false)
                }}
                options={uniqBy(nappiData, 'name').map((i) => ({
                  value: i.name,
                  label: i.name
                }))}
                className='desktop-allergy-search'
                notFoundContent={
                  nappiLoading ? (
                    <div className='search-loading'>
                      <FontAwesomeIcon icon={faPills} spin />
                      <span>Searching...</span>
                    </div>
                  ) : null
                }
              />
            </Modal>
          </div>
        </>
      ) : selectedCategory === 'food' ? (
        <>
          <DesktopSectionHeader
            icon={faAppleAlt}
            title='Food Allergies'
            subtitle="Select any foods you're allergic to"
            onBack={() => setSelectedCategory(null)}
          />
          <div className='desktop-allergies-content'>
            <Card className='desktop-allergy-section-card'>
              <Row gutter={[16, 16]}>
                {COMMON_FOOD_ALLERGIES.map((allergy) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={allergy.id}>
                    <div
                      className={`desktop-allergy-option ${
                        selectedFoodAllergies.includes(allergy.id) ? 'selected' : ''
                      }`}
                      role='checkbox'
                      onClick={() => handleFoodOptionClick(allergy)}
                      aria-checked={selectedFoodAllergies.includes(allergy.id)}
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleFoodOptionClick(allergy)
                        }
                      }}
                    >
                      <div className='desktop-allergy-icon'>
                        <FontAwesomeIcon icon={faAppleAlt} />
                      </div>
                      <span className='desktop-allergy-name'>{allergy.label}</span>
                      <Form.Item
                        name={['allergies', 'food', allergy.id]}
                        valuePropName='checked'
                        style={{ display: 'none' }}
                      >
                        <input type='checkbox' />
                      </Form.Item>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>

            <div className='desktop-additional-allergies'>
              <Button type='primary' className='desktop-add-allergy-btn' onClick={() => setIsModalVisible(true)}>
                Add Food Allergies
              </Button>

              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                {additionalFoodAllergies.map((food, index) => (
                  <Col key={index}>
                    <Tag closable onClose={() => removeFoodAllergy(food)} className='desktop-allergy-tag'>
                      {food}
                    </Tag>
                  </Col>
                ))}
              </Row>
            </div>

            <Modal
              title='Add Food Allergies'
              open={isModalVisible}
              onOk={handleAddFoodAllergy}
              onCancel={() => setIsModalVisible(false)}
              className='desktop-allergies-modal'
            >
              <Input
                placeholder='Type food allergy and press Enter'
                value={newFoodAllergy}
                onChange={(e) => setNewFoodAllergy(e.target.value)}
                onPressEnter={handleAddFoodAllergy}
                className='desktop-allergy-input'
              />
            </Modal>
          </div>
        </>
      ) : selectedCategory === 'general' ? (
        <>
          <DesktopSectionHeader
            icon={faAllergies}
            title='General Allergies'
            subtitle='Select any environmental or other allergies'
            onBack={() => setSelectedCategory(null)}
          />
          <div className='desktop-allergies-content'>
            <Card className='desktop-allergy-section-card'>
              <Row gutter={[16, 16]}>
                {COMMON_GENERAL_ALLERGIES.map((allergy) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={allergy.id}>
                    <div
                      className={`desktop-allergy-option ${
                        selectedGeneralAllergies.includes(allergy.id) ? 'selected' : ''
                      }`}
                      role='checkbox'
                      onClick={() => handleGeneralOptionClick(allergy)}
                      aria-checked={selectedGeneralAllergies.includes(allergy.id)}
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleGeneralOptionClick(allergy)
                        }
                      }}
                    >
                      <div className='desktop-allergy-icon'>
                        <FontAwesomeIcon icon={faAllergies} />
                      </div>
                      <span className='desktop-allergy-name'>{allergy.label}</span>
                      <Form.Item
                        name={['allergies', 'general', allergy.id]}
                        valuePropName='checked'
                        style={{ display: 'none' }}
                      >
                        <input type='checkbox' />
                      </Form.Item>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card>

            <div className='desktop-additional-allergies'>
              <Button type='primary' className='desktop-add-allergy-btn' onClick={() => setIsModalVisible(true)}>
                Add General Allergies
              </Button>

              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                {additionalGeneralAllergies.map((allergy, index) => (
                  <Col key={index}>
                    <Tag closable onClose={() => removeGeneralAllergy(allergy)} className='desktop-allergy-tag'>
                      {allergy}
                    </Tag>
                  </Col>
                ))}
              </Row>
            </div>

            <Modal
              title='Add General Allergies'
              open={isModalVisible}
              onOk={handleAddGeneralAllergy}
              onCancel={() => setIsModalVisible(false)}
              className='desktop-allergies-modal'
            >
              <Input
                placeholder='Type general allergy and press Enter'
                value={newGeneralAllergy}
                onChange={(e) => setNewGeneralAllergy(e.target.value)}
                onPressEnter={handleAddGeneralAllergy}
                className='desktop-allergy-input'
              />
            </Modal>
          </div>
        </>
      ) : null}

      <DesktopMedicalHistoryNav
        loading={loading}
        prevFunction={() => (selectedCategory ? setSelectedCategory(null) : setFacet(null))}
        nextFunction={() => handleSave(true)}
        prevText={selectedCategory ? 'Go Back' : 'Back to Overview'}
        nextText='Save Allergies'
      />
    </div>
  )
}

export default DesktopAllergies
